import {
  Clock,
  Dumbbell,
  Users,
  Trophy,
  Sparkles,
  CheckCircle2,
  CalendarDays,
  HandHeart,
  ArrowRight,
} from "lucide-react";

const programImg =
  "https://images.pexels.com/photos/5407033/pexels-photo-5407033.jpeg";

const galleryImgs = [
  "https://images.pexels.com/photos/15476801/pexels-photo-15476801.jpeg",
  "https://images.pexels.com/photos/12765717/pexels-photo-12765717.jpeg",
  "https://images.pexels.com/photos/13965302/pexels-photo-13965302.jpeg",
];

const phases = [
  {
    icon: Dumbbell,
    time: "10 min",
    title: "Warm-Up",
    text: "Stretching, fun movement games, and a whole lot of high-fives to get energy going.",
  },
  {
    icon: Sparkles,
    time: "20 min",
    title: "Skill Drills",
    text: "Dribbling, passing, and shooting — adapted to each player's pace with support from student volunteers.",
  },
  {
    icon: Users,
    time: "15 min",
    title: "Team Play",
    text: "Small-sided games that teach teamwork, positioning, and how to cheer for your teammates.",
  },
  {
    icon: Trophy,
    time: "15 min",
    title: "Game Time",
    text: "A full team scrimmage where every kid plays, scores, and celebrates.",
  },
];

export default function Program() {
  return (
    <div data-testid="program-page">
      {/* HERO */}
      <section className="relative basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-16 md:pt-24 md:pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-extrabold uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              The Program
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
              One hour. A full team. <span className="text-orange-500">Endless fun.</span>
            </h1>
            <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-xl">
              Every UHL session follows a simple, supportive rhythm designed
              to meet kids where they are — and celebrate every win along
              the way.
            </p>

            <div className="mt-8 inline-flex items-start gap-3 bg-green-50 border border-green-200 rounded-2xl px-5 py-4 max-w-xl">
              <span className="w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center font-black flex-shrink-0">
                $0
              </span>
              <div>
                <p className="font-black text-green-900">
                  Completely free for every family.
                </p>
                <p className="text-sm font-semibold text-green-800/80">
                  Jerseys, equipment, and coaching are all included.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/10 border-[6px] border-white">
            <img
              src={programImg}
              alt="Indoor basketball court"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* SESSION BREAKDOWN */}
      <section className="py-16 md:py-24 bg-white" data-testid="session-flow">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              How sessions work
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              A rhythm that works for every player.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {phases.map(({ icon: Icon, time, title, text }, i) => (
              <div
                key={title}
                data-testid={`phase-${i}`}
                className="relative bg-[#FAFAFA] rounded-3xl p-7 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 hover:-translate-y-1 transition-all"
              >
                <span className="absolute -top-3 left-7 bg-orange-500 text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">
                  Step {i + 1}
                </span>
                <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="mt-4 flex items-center gap-2 text-slate-500 text-sm font-bold">
                  <Clock className="w-4 h-4" />
                  {time}
                </div>
                <h3 className="mt-1 text-xl font-black text-slate-900">{title}</h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]" data-testid="schedule-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
            Schedule
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            When we'll play.
          </h2>

          <div
            data-testid="schedule-card"
            className="mt-10 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-blue-900/5 p-10 md:p-14"
          >
            <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto">
              <CalendarDays className="w-7 h-7 text-blue-600" />
            </div>
            <p className="mt-6 text-xl sm:text-2xl font-black text-slate-900">
              Sample schedule — final times will be shared after
              registration.
            </p>
            <p className="mt-4 text-slate-600 font-medium leading-relaxed max-w-xl mx-auto">
              Practice times may vary as we build the program. Once you
              register, we'll email you with the confirmed schedule and
              everything you need to know.
            </p>
          </div>
        </div>
      </section>

      {/* SUPPORT + GALLERY */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
              A supportive environment
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Volunteers who cheer louder than anyone.
            </h2>
            <p className="mt-5 text-lg text-slate-600 font-medium leading-relaxed">
              Student volunteers from our school club support players
              throughout each session — passing the ball, offering
              encouragement, and celebrating every shot.
            </p>

            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-5 py-4">
              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                All sessions are supervised to ensure a safe and positive
                environment for every player.
              </p>
            </div>

            <div className="mt-3 flex items-start gap-3 rounded-2xl bg-orange-50 border border-orange-100 px-5 py-4">
              <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-slate-700 leading-relaxed">
                We aim to keep group sizes manageable so each player gets
                the attention they need.
              </p>
            </div>

            <ul className="mt-6 space-y-3">
              {[
                "Student volunteers from our school club, passionate about helping",
                "No experience required — just a positive attitude and willingness to help",
                "Calm, predictable structure that kids can count on",
                "Parents welcome to stay, cheer, and connect with other families",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-slate-700 font-semibold">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Reserve%20My%20Spot%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20reserve%20a%20spot%20for%20my%20player.%0A%0AParent%20name%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AAny%20notes%3A%20"
                data-testid="program-register-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 transition-all font-bold px-7 py-3 rounded-full"
              >
                📩 Reserve Your Spot <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Volunteer%20Early%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20volunteer.%0A%0AName%3A%20%0ASchool%20%26%20grade%3A%20"
                data-testid="program-volunteer-btn"
                className="inline-flex items-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-all font-bold px-7 py-3 rounded-full"
              >
                <HandHeart className="w-4 h-4" /> 📩 Volunteer Early
              </a>
            </div>
          </div>

          <div
            className="grid grid-cols-2 gap-4"
            data-testid="program-gallery"
          >
            {galleryImgs.map((src, i) => (
              <div
                key={i}
                className={`rounded-3xl overflow-hidden border border-slate-100 shadow-lg shadow-blue-900/5 ${
                  i === 0 ? "col-span-2" : ""
                }`}
              >
                <img
                  src={src}
                  alt={`Program gallery ${i + 1}`}
                  className={`w-full object-cover hover:scale-105 transition-transform duration-700 ${
                    i === 0 ? "h-64" : "h-44"
                  }`}
                />
              </div>
            ))}
            <p className="col-span-2 text-xs font-semibold text-slate-500 italic mt-1">
              Real photos from our program will be added as we launch.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
